import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ChevronRight } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import SideDrawer from '../components/SideDrawer';
import Button from '../components/ui/Button';
import FieldSelect from '../components/ui/FieldSelect';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { documentReferenceLabel, motorTypeLabel } from '../utils/equipmentQuality';
import type {
  EquipmentDocument,
  EquipmentDocumentFile,
  EquipmentModelDetail,
  EquipmentModelFact,
  EquipmentModelRow,
  EquipmentModelsResponse,
} from '../types';

/**
 * Plan 119 Phase 3 (FS-627) — the equipment model catalog, read-only.
 *
 * One row per (manufacturer, model number) the fleet has been seen carrying,
 * with the manufacturer documents behind it and the facts read out of them.
 * The resolver reads this catalog to fill in a system's motor family, tonnage
 * and rated external static pressure, so an operator looking at a surprising
 * value on a device needs to be able to see the document it came from.
 *
 * Nothing on this page writes. Research is done outside the console and
 * loaded with the catalog seed; there is no create, edit or delete here.
 */

const PAGE_SIZE = 100;

/** How far the research on a model got, in words rather than in codes. */
const LOOKUP_STATUS_LABELS: Record<string, string> = {
  queued: 'Not researched yet',
  resolved: 'Facts on file',
  ambiguous: 'Literature does not settle it',
  no_source: 'No document found',
  // FS-786: until this status existed such a row read "No document found",
  // which sent people looking for literature already in our bucket.
  document_no_facts: 'Document found, no facts read',
  not_a_model: 'Not a model number',
};
const LOOKUP_STATUSES = Object.keys(LOOKUP_STATUS_LABELS);
const lookupStatusLabel = (s: string | null) =>
  (s && LOOKUP_STATUS_LABELS[s]) || (s ?? 'unknown');

/** What each research status means, for the hover text beside its words. */
const LOOKUP_STATUS_TITLES: Record<string, string> = {
  queued: 'The research worker has not looked this model up yet.',
  resolved: 'The research found facts for this model and they are on file.',
  ambiguous: 'The research found literature, and it does not settle which product this is.',
  no_source: 'The research searched and found no manufacturer document for this model.',
  document_no_facts:
    "The research found the manufacturer's document and stored a copy, and no fact read from it passed the citation check. The document is listed on the model; its facts still need reading, and that is on us.",
  not_a_model: 'The entry is not a model number at all.',
};
const lookupStatusTitle = (s: string | null) =>
  (s && LOOKUP_STATUS_TITLES[s]) || 'A research status this console has no description for.';

const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  furnace: 'Furnace',
  air_handler: 'Air handler',
  outdoor_condenser: 'Outdoor unit',
  heat_pump: 'Heat pump',
  indoor_coil: 'Indoor coil',
  package_unit: 'Packaged unit',
  ductless: 'Ductless',
  unknown: 'Unknown',
};
const equipmentTypeLabel = (t: string | null) =>
  (t && EQUIPMENT_TYPE_LABELS[t]) || (t ?? 'unknown');

/** What each fact in the catalog is, in words. */
const FACT_LABELS: Record<string, string> = {
  motor_family: 'Blower motor family',
  cooling_tonnage: 'Cooling tonnage',
  cooling_btu: 'Cooling capacity',
  heating_btu: 'Heating capacity',
  rated_esp: 'Rated external static pressure',
  blower_table: 'Blower table',
  airflow_cfm_nominal: 'Nominal airflow',
  stages: 'Stages',
  refrigerant: 'Refrigerant',
};
const factLabel = (f: string) => FACT_LABELS[f] ?? f.replace(/_/g, ' ');

const UNIT_LABELS: Record<string, string> = {
  in_wc: 'in. w.c.',
  pa: 'Pa',
  Pa: 'Pa',
  tons: 'tons',
  btuh: 'BTU/h',
  cfm: 'CFM',
};
const unitLabel = (u: string | null) => (u ? UNIT_LABELS[u] ?? u : '');

/** `rated_esp` kinds. Neither is a filter end-of-life threshold. */
const FACT_KIND_LABELS: Record<string, string> = {
  rating_point: 'rating point',
  furnace_limit: 'furnace limit',
};

const unknown = <span className="text-gray-600">unknown</span>;
const text = (v: string | null | undefined) => (v && v.trim().length > 0 ? v : unknown);
const day = (v: string | null | undefined) => (v ? v.slice(0, 10) : null);

/** The fact's value as a person reads it, with its unit. */
const factValue = (f: EquipmentModelFact): string => {
  if (f.fact === 'motor_family' && f.value_text) return motorTypeLabel(f.value_text);
  if (f.value_text && f.value_text.trim().length > 0) return f.value_text;
  if (f.value_num !== null && f.value_num !== undefined) {
    const unit = unitLabel(f.unit);
    return unit ? `${f.value_num} ${unit}` : String(f.value_num);
  }
  if (f.value_json) return 'a table, held in the catalog';
  return 'no value recorded';
};

/**
 * Why a request failed, said rather than swallowed. The status alone is not
 * much, but it is the difference between "you are not allowed" and "the API
 * is down", which are different things to do next.
 */
const failureDetail = (err: unknown): string => {
  const e = err as { response?: { status?: number; data?: unknown }; message?: string };
  const status = e?.response?.status;
  const body = e?.response?.data;
  const fromBody =
    body && typeof body === 'object' && typeof (body as { message?: unknown }).message === 'string'
      ? (body as { message: string }).message
      : '';
  const message = fromBody || (typeof e?.message === 'string' ? e.message : '');
  if (status && message) return `${status} — ${message}`;
  if (status) return `HTTP ${status}`;
  return message || 'no response from the device API';
};

const isForbidden = (err: unknown) => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403;
};

/**
 * A file size an operator reads before deciding to open it. A document with
 * no recorded size says so — a blank would read as a small file.
 */
const byteSizeLabel = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) {
    return 'size not recorded';
  }
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * `?model_id=` as a model id, or null. A link written by hand or cut short in
 * an email is not a request for model NaN — nothing is opened and the catalog
 * is shown as it would have been without the parameter.
 */
const parseModelId = (raw: string | null): number | null => {
  if (!raw || !/^\d+$/.test(raw)) return null;
  const id = Number(raw);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th scope="col" className="text-left py-2 px-2 border-b whitespace-nowrap">
    {children}
  </th>
);

const EquipmentCatalogPage: React.FC = () => {
  /**
   * The query string is how the rest of the console points here: a model
   * number anywhere in the app links to `?model_id=` when the record matched
   * the catalog and `?q=` when it did not, so the operator lands on the model
   * or on the search that says whether we hold one. It is read once, on the
   * first render, and written back as the operator works — replaced, not
   * pushed, so Back leaves the catalog rather than replaying every model they
   * opened.
   */
  const [searchParams, setSearchParams] = useSearchParams();

  const [rows, setRows] = useState<EquipmentModelRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  // Three distinguishable failures: not allowed, everything else, and the
  // ordinary empty result — which is not a failure at all.
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null);
  const [errorDetail, setErrorDetail] = useState<string>('');

  // Both seeded from the URL on the first render only. `useState` reads its
  // initial value once, which is exactly the wanted behaviour: after that the
  // page owns them and writes the URL, not the other way round.
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '');
  const [status, setStatus] = useState('');
  const q = useDebouncedValue(search, 300);

  const [openId, setOpenId] = useState<number | null>(() =>
    parseModelId(searchParams.get('model_id')),
  );
  const [detail, setDetail] = useState<EquipmentModelDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string>('');

  const params = useMemo(() => {
    const p: Record<string, string | number> = { limit: PAGE_SIZE, offset };
    if (q.trim()) p.q = q.trim();
    if (status) p.status = status;
    return p;
  }, [q, status, offset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorDetail('');
    try {
      const resp = await axiosInstance.get<EquipmentModelsResponse>(
        `${config.devicesApiUrl}/equipment/models`,
        { params },
      );
      setRows(resp.data.rows);
      setTotal(resp.data.total);
    } catch (err) {
      setError(isForbidden(err) ? 'forbidden' : 'failed');
      setErrorDetail(failureDetail(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // A new search or filter starts at the first page; otherwise page 4 of the
  // old result silently becomes page 4 of the new one.
  useEffect(() => { setOffset(0); }, [q, status]);

  /**
   * The detail hangs off the open model rather than off the click, because a
   * model can be opened without one: a `?model_id=` link opens a model the
   * list may not even contain, and it has to load the same way a clicked row
   * does. Fetching it by id and not from the row is what makes that work.
   */
  useEffect(() => {
    if (openId === null) {
      setDetail(null);
      setDetailError('');
      setDetailLoading(false);
      return;
    }
    let cancelled = false;
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    axiosInstance
      .get<EquipmentModelDetail>(`${config.devicesApiUrl}/equipment/models/${openId}`)
      .then((resp) => { if (!cancelled) setDetail(resp.data); })
      .catch((err) => { if (!cancelled) setDetailError(failureDetail(err)); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [openId]);

  const openModel = (id: number) => setOpenId(id);
  const closeModel = useCallback(() => setOpenId(null), []);

  // Per document, because a drawer lists several and a failure on one says
  // nothing about the others.
  const [fileBusyId, setFileBusyId] = useState<number | null>(null);
  const [fileErrors, setFileErrors] = useState<Record<number, string>>({});

  /**
   * Our own copy of a manufacturer document. The manufacturer's link is kept
   * beside it and is the better one while it works — but the fact in the
   * catalog was read out of the copy we stored, and manufacturers move and
   * withdraw their PDFs.
   *
   * The tab is opened by the click and pointed at the file afterwards. Opened
   * the other way round — after the await — it is no longer the click's tab
   * and a popup blocker is entitled to eat it. The link the server hands back
   * is presigned and short-lived, so it is opened as-is: sending the console's
   * Authorization header with it would be both pointless and a leak.
   */
  const openOurCopy = async (documentId: number) => {
    setFileErrors((prev) => ({ ...prev, [documentId]: '' }));
    setFileBusyId(documentId);
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const resp = await axiosInstance.get<EquipmentDocumentFile>(
        `${config.devicesApiUrl}/equipment/documents/${documentId}/file`,
      );
      const url = resp.data?.url;
      if (!url) throw new Error('the catalog returned no link for this document');
      if (tab) tab.location.href = url;
      else window.open(url, '_blank', 'noopener');
    } catch (err) {
      tab?.close();
      setFileErrors((prev) => ({ ...prev, [documentId]: failureDetail(err) }));
    } finally {
      setFileBusyId(null);
    }
  };

  /**
   * The URL follows what is on screen, so the operator can hand the address
   * bar to somebody else and they see the same thing. Replaced rather than
   * pushed: opening four models in a row should not put four entries between
   * the operator and wherever they came from.
   */
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const wanted = q.trim();
    if (wanted) next.set('q', wanted);
    else next.delete('q');
    if (openId !== null) next.set('model_id', String(openId));
    else next.delete('model_id');
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [q, openId, searchParams, setSearchParams]);

  const shown = rows.length;
  const firstOnPage = total === 0 ? 0 : offset + 1;

  /**
   * The drawer is headed by the model the operator clicked, from the row they
   * clicked it on — so it is named from the moment it opens rather than after
   * the detail lands, and it is still named when the detail will not load.
   */
  const openRow = openId === null ? null : rows.find((r) => r.id === openId) ?? null;
  const openTitle = detail?.model.model_display ?? openRow?.model_display ?? 'Model';

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">Equipment Catalog</h1>
      <p className="text-sm text-gray-600 mb-4">
        What the manufacturers publish for the model numbers our fleet carries:
        the blower motor family, the cooling tonnage and the rated external
        static pressure, each with the document it was read out of. This is
        where a system's motor type and rated pressure come from when they are
        marked as coming from a manufacturer document. Reading only — research
        is loaded with the catalog, not edited here.
      </p>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <Label htmlFor="catalog-q">Search</Label>
          <Input
            id="catalog-q"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Model number or manufacturer"
            title="Match part of a model number or a manufacturer name. Punctuation and case are ignored."
            aria-describedby="catalog-q-help"
            className="mt-1 w-72"
          />
          <p id="catalog-q-help" className="text-xs text-muted-foreground">Match model number or manufacturer; punctuation and case are ignored.</p>
        </div>
        <FieldSelect id="catalog-status" label="Research status"
          help="Filter by how far document research has progressed."
          value={status} onChange={setStatus}
          options={[{ value: '', label: 'Any status' }, ...LOOKUP_STATUSES.map(s => ({ value: s, label: LOOKUP_STATUS_LABELS[s], title: LOOKUP_STATUS_TITLES[s] }))]} />
        <Button
          variant="secondary"
          onClick={load}
          disabled={loading}
          title="Ask the device API for this list again"
        >
          Refresh
        </Button>
      </div>

      {loading && <p className="text-muted-foreground">Loading…</p>}

      {!loading && error === 'forbidden' && (
        <p className="text-status-error-foreground">
          You do not have permission to read the equipment catalog. Ask an
          administrator for device read access.
        </p>
      )}
      {!loading && error === 'failed' && (
        <>
          <p className="text-status-error-foreground">
            The equipment catalog could not be loaded. Try Refresh; if it keeps
            failing the device API is not responding.
          </p>
          <p className="mt-1 text-sm text-red-700 font-mono">{errorDetail}</p>
        </>
      )}
      {!loading && !error && total === 0 && (
        <p className="text-muted-foreground">No model in the catalog matches this search</p>
      )}

      {!loading && !error && total > 0 && (
        <>
          <p className="text-sm text-gray-600 mb-2">
            Showing {firstOnPage}–{offset + shown} of {total}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-card border border-border text-sm">
              <thead className="bg-muted">
                <tr>
                  <Th>Model</Th>
                  <Th>Manufacturer</Th>
                  <Th>Product line</Th>
                  <Th>Equipment</Th>
                  <Th>Research status</Th>
                  <Th>Blower motor</Th>
                  <Th>Rated static</Th>
                  <Th>Tonnage</Th>
                  <Th>Facts</Th>
                  <Th>Aliases</Th>
                  <Th>Documents</Th>
                  <Th>Checked</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b hover:bg-gray-50 ${openId === r.id ? 'bg-blue-50' : ''}`}
                  >
                    <td className="py-2 px-2">
                      <div className="flex items-start gap-1">
                        <span className="font-mono break-words min-w-0">{r.model_display}</span>
                        <Button
                          variant="icon"
                          size="sm"
                          className="shrink-0"
                          aria-label={r.model_display}
                          onClick={() => openModel(r.id)}
                          title="Open this model and read the facts and documents the catalog holds for it"
                        >
                          <ChevronRight data-icon-size="" className="size-4" />
                        </Button>
                      </div>
                    </td>
                    <td className="py-2 px-2">{text(r.manufacturer)}</td>
                    <td className="py-2 px-2">{text(r.product_line)}</td>
                    <td className="py-2 px-2">{equipmentTypeLabel(r.equipment_type)}</td>
                    <td className="py-2 px-2" title={lookupStatusTitle(r.lookup_status)}>
                      {lookupStatusLabel(r.lookup_status)}
                    </td>
                    <td className="py-2 px-2">
                      {r.motor_family ? motorTypeLabel(r.motor_family) : 'not on file'}
                    </td>
                    <td className="py-2 px-2">
                      {r.rated_esp_pa !== null && r.rated_esp_pa !== undefined
                        ? `${r.rated_esp_pa} Pa`
                        : 'not on file'}
                    </td>
                    <td className="py-2 px-2">
                      {r.cooling_tonnage !== null && r.cooling_tonnage !== undefined
                        ? `${r.cooling_tonnage} t`
                        : 'not on file'}
                    </td>
                    <td className="py-2 px-2 tabular-nums">{r.fact_count}</td>
                    <td className="py-2 px-2 tabular-nums">{r.alias_count}</td>
                    <td className="py-2 px-2 tabular-nums">{r.document_count}</td>
                    <td className="py-2 px-2 whitespace-nowrap">
                      {day(r.verified_at) ?? <span className="text-gray-600">not checked</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Button
              variant="secondary"
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              disabled={offset === 0}
              title="Go back one page of models"
            >
              Previous page
            </Button>
            <Button
              variant="secondary"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + shown >= total}
              title="Go on one page of models"
            >
              Next page
            </Button>
          </div>
        </>
      )}

      {/* Over the page, not under the table: with a catalog of any size the
          bottom of the page is off screen, and a detail block rendered down
          there looked like the click did nothing at all. */}
      {openId !== null && (
        <SideDrawer
          title={openTitle}
          subtitle="What the catalog holds for this model, and the documents behind it."
          onClose={closeModel}
          closeLabel="Close the model"
        >
          <div className="p-4" data-testid="equipment-model-detail">
            {detailLoading && <p className="text-muted-foreground mt-2">Opening…</p>}
            {!detailLoading && detailError && (
              <>
                <p className="text-status-error-foreground mt-2">
                  That model could not be opened. The list behind this is still
                  what the catalog returned.
                </p>
                <p className="mt-1 text-sm text-red-700 font-mono">{detailError}</p>
              </>
            )}

            {!detailLoading && !detailError && detail && (
              <>
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mt-3">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Manufacturer</dt>
                    <dd>{text(detail.model.manufacturer)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Product line</dt>
                    <dd>{text(detail.model.product_line)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Equipment</dt>
                    <dd>{equipmentTypeLabel(detail.model.equipment_type)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Research status</dt>
                    <dd title={lookupStatusTitle(detail.model.lookup_status)}>
                      {lookupStatusLabel(detail.model.lookup_status)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Lookup key</dt>
                    <dd className="font-mono break-all">{detail.model.model_normalized}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Checked by</dt>
                    <dd>{text(detail.model.verified_by)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Checked on</dt>
                    <dd>{day(detail.model.verified_at) ?? unknown}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-600">Notes</dt>
                    <dd>{detail.model.notes || 'none'}</dd>
                  </div>
                </dl>

                <h3 className="text-sm font-semibold mt-5 mb-2">
                  What the catalog holds for this model
                </h3>
                {detail.facts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Nothing yet — this model is in the catalog but no fact has been read for it.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table
                      className="min-w-full border border-border text-sm"
                      data-testid="equipment-model-facts"
                    >
                      <thead className="bg-muted">
                        <tr>
                          <Th>Fact</Th>
                          <Th>Value</Th>
                          <Th>Kind</Th>
                          <Th>Confidence</Th>
                          <Th>Applies to</Th>
                          <Th>Document</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.facts.map((f) => (
                          <tr key={f.id} className="border-b">
                            <td className="py-2 px-3">{factLabel(f.fact)}</td>
                            <td className="py-2 px-3">{factValue(f)}</td>
                            <td className="py-2 px-3">
                              {f.kind
                                ? FACT_KIND_LABELS[f.kind] ?? f.kind
                                : <span className="text-gray-600">not recorded</span>}
                            </td>
                            <td className="py-2 px-3">{text(f.confidence)}</td>
                            <td className="py-2 px-3">{f.applicability || 'the whole model'}</td>
                            <td className="py-2 px-3">
                              {documentReferenceLabel(f.document, f.locator)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h3 className="text-sm font-semibold mt-5 mb-2">
                  Other model numbers that resolve here
                </h3>
                {detail.aliases.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None on file.</p>
                ) : (
                  <ul className="text-sm list-disc pl-5" data-testid="equipment-model-aliases">
                    {detail.aliases.map((a) => (
                      <li key={a.alias_normalized}>
                        <span className="font-mono">{a.alias_normalized}</span>
                        {' — '}
                        {a.reason || 'no reason recorded'}
                      </li>
                    ))}
                  </ul>
                )}

                <h3 className="text-sm font-semibold mt-5 mb-2">Documents</h3>
                {detail.documents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">None on file.</p>
                ) : (
                  <ul className="text-sm space-y-1" data-testid="equipment-model-documents">
                    {detail.documents.map((d: EquipmentDocument) => (
                      <li key={d.id}>
                        <a
                          href={d.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open the manufacturer's own copy of this document in a new tab"
                          className="text-blue-700 hover:underline"
                        >
                          {d.title || d.source_url}
                        </a>
                        {' · '}
                        <Button
                          variant="secondary"
                          onClick={() => openOurCopy(d.id)}
                          disabled={fileBusyId === d.id}
                          title={detail.facts.length > 0
                            ? 'Open the copy we stored of this document in a new tab. This is the copy the facts above were read out of.'
                            : 'Open the copy we stored of this document in a new tab. The catalog research read this copy; no fact from it is on file yet.'}
                        >
                          {fileBusyId === d.id ? 'Opening our copy…' : 'Our copy'}
                        </Button>

                        <span className="text-muted-foreground">
                          {d.revision ? ` rev ${d.revision}` : ' revision not recorded'}
                          {d.pages ? `, ${d.pages} pages` : ''}
                          {`, ${byteSizeLabel(d.byte_size)}`}
                          {day(d.retrieved_at) ? `, retrieved ${day(d.retrieved_at)}` : ''}
                        </span>
                        {fileErrors[d.id] && (
                          <span className="mt-0.5 block text-xs text-red-700" role="alert">
                            Our copy could not be opened — {fileErrors[d.id]}. The
                            manufacturer's link beside it is untouched.
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </SideDrawer>
      )}
    </div>
  );
};

export default EquipmentCatalogPage;
